import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ChartSeries {
  dataKey: string;
  name: string;
  color: string;
  yAxisId?: string;
  dashed?: boolean;
  isCurrency?: boolean;
}

interface EvolutionChartProps {
  title: string;
  data: any[];
  series: ChartSeries[];
  xDataKey?: string;
  height?: number;
  dualAxis?: boolean;
  rightAxisLabel?: string;
  leftAxisFormatter?: (value: number) => string;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
};

export const EvolutionChart: React.FC<EvolutionChartProps> = ({
  title,
  data,
  series,
  xDataKey = 'monthName',
  height = 300,
  dualAxis = false,
  leftAxisFormatter,
}) => {
  const isMobile = useIsMobile();
  const chartHeight = isMobile ? 220 : height;
  const tickFontSize = isMobile ? 10 : 12;

  return (
    <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 sm:tw-p-6 tw-overflow-hidden">
      <h3 className="tw-text-sm sm:tw-text-base md:tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-3 sm:tw-mb-4">
        {title}
      </h3>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey={xDataKey}
            tick={{ fill: '#6b7280', fontSize: tickFontSize }}
            axisLine={{ stroke: '#d1d5db' }}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: '#6b7280', fontSize: tickFontSize }}
            axisLine={{ stroke: '#d1d5db' }}
            tickFormatter={leftAxisFormatter}
            width={isMobile ? 40 : 60}
          />
          {dualAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: '#6b7280', fontSize: tickFontSize }}
              axisLine={{ stroke: '#d1d5db' }}
              tickFormatter={formatCurrency}
              width={isMobile ? 50 : 70}
            />
          )}
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: isMobile ? '8px' : '12px',
              fontSize: isMobile ? '11px' : '14px',
            }}
            formatter={(value: number, name: string) => {
              const s = series.find((s) => s.name === name);
              if (s?.isCurrency) {
                return [formatCurrency(value), name];
              }
              return [value, name];
            }}
          />
          <Legend wrapperStyle={{ fontSize: isMobile ? '10px' : '14px' }} />
          {series.map((s) => (
            <Line
              key={s.dataKey}
              type="monotone"
              dataKey={s.dataKey}
              stroke={s.color}
              strokeWidth={2}
              name={s.name}
              yAxisId={s.yAxisId || 'left'}
              dot={{ r: isMobile ? 3 : 4 }}
              activeDot={{ r: isMobile ? 4 : 6 }}
              strokeDasharray={s.dashed ? '5 5' : undefined}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
